<template>
  <div
    :style="cssColorVars"
    class="a-tabs"
    role="tablist"
    @keydown.left.exact.stop="handleFocusPrev"
    @keydown.right.exact.stop="handleFocusNext"
  >
    <button
      v-for="(tab, idx) in statusList"
      :id="`tab-${tab.value}`"
      :key="`tab-${idx}`"
      ref="tabs"
      :value="tab.value"
      class="tab"
      z-index="0"
      :aria-controls="tab.panelId || undefined"
      :aria-selected="String(isActive(tab))"
      :aria-label="`${ariaLabel} of ${tab.fullName}`"
      role="tab"
      :tabindex="isActive(tab) ? 0 : -1"
      :class="[{ current: isActive(tab), tab }]"
      @click="changeTab($event, idx)"
    >
      <span
        :id="`tab-title-${tab.value}`"
        class="tab-title"
        z-index="-1"
        tabindex="-1"
      >
        {{ tab.label }}
      </span>
      <span
        v-if="showStatusCount"
        z-index="-1"
        class="tab-counter"
      >
        {{ tab.count < 99 ? tab.count : '99+' }}
      </span>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue';

defineOptions({ name: 'ATabs' });

const props = defineProps({
  statusList: {
    type: Array,
    required: true,
  },

  showStatusCount: Boolean,
  selected: {
    type: [String, Number],
    default: 0,
  },
  confirm: Boolean,
  confirmTitle: {
    type: String,
    default: '',
  },
  confirmText: {
    type: String,
    default: '',
  },
  highlightsColor: {
    type: String,
    default: '#004990',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  currentFocusIdx: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['focus-next', 'switch-tabs']);

const currentStatus = ref('');
const currentEvent = ref(null);
const destination = ref({});
const isConfirming = ref(false);

const cssColorVars = computed(() => ({
  '--tab-border-bottom-active': `${props.highlightsColor}`,
  '--tab-border-bottom': `${props.highlightsColor}1A`,
  '--tab-counter': `${props.highlightsColor}`,
  '--tab-font-color': '#707070',
  '--tab-font-color-hover': `${props.highlightsColor}`,
  '--tab-font-color-active': `${props.highlightsColor}`,
}));

function isActive(status) {
  return status.idx === props.selected;
}

function handleFocusPrev() {
  emit('focus-next', props.currentFocusIdx - 1);
}

function handleFocusNext() {
  emit('focus-next', props.currentFocusIdx + 1);
}

function changeTab(e, tab) {
  currentEvent.value = e;
  if (props.confirm) {
    destination.value = tab;
    isConfirming.value = true;
  } else {
    currentStatus.value = tab;
    emit('switch-tabs', tab);
  }
}

onBeforeMount(() => {
  currentStatus.value = props.statusList.find((s) => s.idx === props.selected) || props.statusList[0];
});
</script>

<style lang="scss" scoped>
@use '../../global/scss/color_vars';
@use '../scss/mixins/flex_mixin';
@use '../scss/mixins/breakpoints';

.a-tabs {
  --tab-bg-color: transparent;

  @include flex_mixin.flex-row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  .tab {
    border-radius: 0;
    border: none;
    border-bottom: 4px solid var(--tab-border-bottom);
    display: flex;
    align-items: center;
    color: (var(--tab-font-color));
    background-color: var(--tab-bg-color);
    outline: none;
    cursor: pointer;
    font-style: normal;
    font-weight: bold;
    font-family: inherit;
    line-height: 32px;
    letter-spacing: 0.8px;
  }
  .tab:hover,
  .tab:hover:not(.current) .status-count {
    color: var(--tab-font-color-hover);
  }
  .tab.current {
    border-bottom: 4px solid var(--tab-border-bottom-active);
    color: var(--tab-font-color-active);
    font-weight: 900;
  }
  @media (max-width: 360px) {
    .tab {
      padding: 4px;
      width: 50px;
    }
  }
  @include breakpoints.screen('xs') {
    .tab {
      padding: 8px;
      width: 55px;
    }
  }
  @include breakpoints.screen('sm') {
    .tab {
      padding: 12px 6px 8px 6px;
    }
  }
  @media (min-width: breakpoints.$lg-min-width) {
    .tab {
      padding: 12px 8px 8px 8px;
    }
  }
  .tab-title {
    font-size: 24px;
  }
  .tab:focus,
  .tab-title {
    outline: none;
  }
  .tab:focus-visible > .tab-title {
    outline: var(--tab-font-color-active) auto 1px;
  }
  .tab-counter {
    background-color: var(--tab-counter);
    color: white;
    margin-left: 4px;
    border-radius: 12.5px;
    width: 42px;
    height: 24px;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3px 6px;
    box-sizing: border-box;
  }
}
</style>
